/**
 * Chamadas HTTP dos pedidos de compra.
 *
 * Papel: aqui ficam as cinco chamadas de `/api/pedidos-compra` que a tela usa —
 * listar, criar, alterar, excluir e buscar a imagem de um item — e
 * `buscarClassificacaoFinanceira`, que traz as categorias e os favorecidos que
 * o cartão financeiro de um item oferece.
 *
 * A rota de obter um pedido existe na API e não tem chamada aqui: a listagem já
 * traz os pedidos inteiros, com os itens aninhados, e uma segunda busca pelo
 * mesmo dado só acrescentaria uma requisição.
 *
 * Como usar: a listagem devolve o array inteiro com os itens aninhados, **e sem
 * as imagens**. O que cada item traz é `temFoto`; a imagem vem por
 * `buscarFotoDoItem`, uma por vez e só quando alguém a abre.
 */
import {
  type ClassificacaoFinanceira,
  type NovoPedidoCompra,
  type PedidoCompra,
  classificacaoFinanceiraSchema,
  fotoDoItemSchema,
  pedidoCompraSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDePedidosSchema = z.array(pedidoCompraSchema);

/**
 * Lê os pedidos, do mais recente para o mais antigo.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os pedidos com os itens aninhados, sem as imagens.
 */
export function buscarPedidos(sinal?: AbortSignal): Promise<PedidoCompra[]> {
  return clienteApi.get('/pedidos-compra', { schema: listaDePedidosSchema, sinal });
}

/**
 * Lê as categorias de despesa do plano de contas e os perfis dos favorecidos do
 * caixa.
 *
 * A rota é de Compras, e não a de plano de contas de Finanças: quem tem só o
 * módulo de compras precisa dos selects do item tanto quanto quem tem os dois.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As categorias com as subcategorias, os favorecidos e os padrões.
 */
export function buscarClassificacaoFinanceira(
  sinal?: AbortSignal,
): Promise<ClassificacaoFinanceira> {
  return clienteApi.get('/compras/classificacao-financeira', {
    schema: classificacaoFinanceiraSchema,
    sinal,
  });
}

/**
 * Lê a imagem de um item.
 *
 * É a única chamada que traz imagem, e por isso a única que pode custar
 * megabytes. Quem a dispara é a abertura da foto, e nunca a montagem de lista.
 *
 * @param itemId - Identificador da linha em `itens_pedido_compra`.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A imagem em base64, ou `null` quando o item não tem nenhuma.
 */
export function buscarFotoDoItem(
  itemId: string,
  sinal?: AbortSignal,
): Promise<{ foto: string | null }> {
  return clienteApi.get(`/pedidos-compra/itens/${encodeURIComponent(itemId)}/foto`, {
    schema: fotoDoItemSchema,
    sinal,
  });
}

/**
 * Cria um pedido com os itens dele.
 *
 * @param dados - O pedido preenchido, já validado.
 * @returns O pedido como ficou gravado.
 */
export function criarPedido(dados: NovoPedidoCompra): Promise<PedidoCompra> {
  return clienteApi.post('/pedidos-compra', dados, { schema: pedidoCompraSchema });
}

/**
 * Substitui todos os campos de um pedido, e os itens dele por inteiro.
 *
 * Um item que não vier no corpo deixa de existir. A imagem de um item é o único
 * campo que sobrevive a não vir: ausente significa "não mexa", e string vazia é
 * o que apaga.
 *
 * **Esta chamada pode escrever no caixa.** O pedido que chega a `comprado` gera
 * uma despesa por item, e o que sai de `comprado` apaga as que gerou quando
 * `lancamentos` vem como `excluir`. Sem esse argumento as despesas ficam onde
 * estão, e o pedido retrocede do mesmo jeito.
 *
 * @param id - Identificador da linha em `pedidos_compra`.
 * @param dados - O pedido inteiro, e não apenas os campos alterados.
 * @param lancamentos - O que fazer com as despesas ao sair de `comprado`;
 *   omitido, elas são mantidas.
 * @returns O pedido como ficou gravado.
 */
export function atualizarPedido(
  id: string,
  dados: NovoPedidoCompra,
  lancamentos?: 'excluir' | 'manter',
): Promise<PedidoCompra> {
  return clienteApi.put(`/pedidos-compra/${encodeURIComponent(id)}`, dados, {
    schema: pedidoCompraSchema,
    params: lancamentos === undefined ? undefined : { lancamentos },
  });
}

/**
 * Apaga um pedido, com os itens dele.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `pedidos_compra`.
 */
export function excluirPedido(id: string): Promise<void> {
  return clienteApi.remover(`/pedidos-compra/${encodeURIComponent(id)}`);
}
